"use client"

import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import {
  Brain,
  TrendingUp,
  FileText,
  Clock,
  Search,
  Briefcase,
  Users,
  Target,
  ShieldCheck,
  Mail,
  Building,
  BarChart3,
  Sparkles,
  Zap,
  CheckCircle2,
  ArrowRight,
  GraduationCap,
  Award
} from "lucide-react"
import Link from "next/link"
import { useTranslation } from "react-i18next"
import { motion, useScroll, useTransform, useInView } from "framer-motion"
import { useRef, useEffect, useState } from "react"

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 }
  }
}

const staggerContainer = {
  visible: {
    transition: { staggerChildren: 0.1 }
  }
}

const scaleIn = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: { duration: 0.5 }
  }
}

// Floating animation for decorative elements
const floatingAnimation = {
  y: [0, -20, 0],
  transition: {
    duration: 3,
    repeat: Infinity
  }
}

// Hero Section Component
function HeroSection({ isRTL }: { isRTL: boolean }) {
  const ref = useRef(null)
  const { scrollY } = useScroll()
  const y = useTransform(scrollY, [0, 500], [0, 150])
  const opacity = useTransform(scrollY, [0, 300], [1, 0])

  return (
    <section
      ref={ref}
      className="relative min-h-[70vh] flex items-center justify-center overflow-hidden bg-gradient-to-b from-primary/10 via-primary/5 to-transparent"
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          animate={floatingAnimation}
          className="absolute top-20 left-10 w-72 h-72 bg-primary/20 rounded-full blur-3xl"
          style={{ y, opacity }}
        />
        <motion.div
          animate={{ ...floatingAnimation, transition: { ...floatingAnimation.transition, delay: 1 } }}
          className="absolute bottom-20 right-10 w-96 h-96 bg-primary/10 rounded-full blur-3xl"
          style={{ y, opacity }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,hsl(var(--background))_100%)]" />
      </div>

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:48px_48px]" />

      {/* Content */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
        className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
      >
        <motion.div variants={fadeInUp}>
          <Badge className="mb-6 px-4 py-2 text-sm font-medium bg-primary/10 hover:bg-primary/20 border-primary/20">
            <Sparkles className="w-4 h-4 mr-2 inline-block" />
            AI-Powered Platform
          </Badge>
        </motion.div>

        <motion.h1
          variants={fadeInUp}
          className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight mb-6 bg-gradient-to-r from-primary via-primary/80 to-primary/60 bg-clip-text text-transparent"
        >
          Platform Features
        </motion.h1>

        <motion.p
          variants={fadeInUp}
          className="text-xl lg:text-2xl text-muted-foreground max-w-3xl mx-auto mb-8 leading-relaxed"
        >
          Comprehensive AI-powered tools designed for modern recruitment excellence
        </motion.p>

        <motion.div
          variants={fadeInUp}
          className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground"
        >
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-primary" />
            <span className="font-medium">12 Core Features</span>
          </div>
          <div className="w-1 h-1 rounded-full bg-muted-foreground/30" />
          <div className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-primary" />
            <span className="font-medium">9 AI Scoring Criteria</span>
          </div>
          <div className="w-1 h-1 rounded-full bg-muted-foreground/30" />
          <div className="flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" />
            <span className="font-medium">Unlimited Potential</span>
          </div>
        </motion.div>
      </motion.div>
    </section>
  )
}

// Interactive Feature Showcase Component
function InteractiveShowcase({ isRTL }: { isRTL: boolean }) {
  const features = [
    {
      icon: Brain,
      title: "AI-Powered CV Analysis",
      description: "Upload multiple resumes and receive instant AI-powered scores across 9 critical criteria. Get detailed insights on candidate fit, experience relevance, and hiring potential.",
      benefits: [
        "Instant scoring across 9 criteria",
        "Objective, data-driven decisions",
        "Batch processing for efficiency",
        "Detailed strength/weakness analysis"
      ],
      visual: "scores"
    },
    {
      icon: TrendingUp,
      title: "Smart Comparison Engine",
      description: "Compare top candidates side-by-side with interactive analytics. Identify the perfect match for your team using data visualization that highlights skills, experience, and qualifications.",
      benefits: [
        "Visual side-by-side comparison",
        "Skill gap analysis",
        "Experience mapping",
        "Team fit assessment"
      ],
      visual: "comparison"
    },
    {
      icon: FileText,
      title: "Job Enhancement AI",
      description: "Create compelling job postings that attract top talent. Our AI analyzes your requirements and optimizes content for maximum appeal and better candidate quality.",
      benefits: [
        "AI-optimized descriptions",
        "Keyword enhancement",
        "Industry best practices",
        "Higher quality applicants"
      ],
      visual: "enhancement"
    }
  ]

  return (
    <section className="relative py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="space-y-24">
          {features.map((feature, index) => (
            <InteractiveFeatureCard
              key={index}
              feature={feature}
              index={index}
              isRTL={isRTL}
            />
          ))}
        </div>
      </div>
    </section>
  )
}

// Individual Interactive Feature Card
function InteractiveFeatureCard({
  feature,
  index,
  isRTL
}: {
  feature: any
  index: number
  isRTL: boolean
}) {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })
  const isEven = index % 2 === 0

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={fadeInUp}
      className={`grid lg:grid-cols-2 gap-12 items-center ${!isEven && 'lg:grid-flow-dense'}`}
    >
      {/* Content Side */}
      <div className={!isEven ? 'lg:col-start-2' : ''}>
        <motion.div
          variants={scaleIn}
          className="inline-flex p-3 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 mb-6"
        >
          <feature.icon className="w-8 h-8 text-primary" />
        </motion.div>

        <h3 className="text-3xl lg:text-4xl font-bold mb-4">{feature.title}</h3>
        <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
          {feature.description}
        </p>

        <ul className="space-y-3 mb-8">
          {feature.benefits.map((benefit: string, idx: number) => (
            <motion.li
              key={idx}
              variants={fadeInUp}
              className="flex items-start gap-3"
            >
              <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <span className="text-muted-foreground">{benefit}</span>
            </motion.li>
          ))}
        </ul>
      </div>

      {/* Visual Side */}
      <div className={!isEven ? 'lg:col-start-1 lg:row-start-1' : ''}>
        <motion.div
          variants={scaleIn}
          className="relative aspect-square rounded-3xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-8 backdrop-blur-xl border border-primary/10 overflow-hidden group hover:shadow-2xl hover:shadow-primary/20 transition-all duration-500"
        >
          {/* Mock Visual Element */}
          <div className="absolute inset-0 flex items-center justify-center">
            <feature.icon className="w-32 h-32 text-primary/20 group-hover:scale-110 transition-transform duration-500" />
          </div>

          {/* Animated Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/0 via-primary/5 to-primary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        </motion.div>
      </div>
    </motion.div>
  )
}

// Feature Categories Component
function FeatureCategories({ isRTL }: { isRTL: boolean }) {
  const categories = [
    {
      title: "For Employers",
      icon: Building,
      count: 8,
      features: [
        { icon: Brain, title: "AI CV Evaluation", desc: "Instant multi-CV scoring across 9 criteria" },
        { icon: TrendingUp, title: "Applicant Comparison", desc: "Side-by-side analytics and insights" },
        { icon: FileText, title: "Job Enhancement", desc: "AI-optimized posting descriptions" },
        { icon: Clock, title: "Interview Scheduling", desc: "Integrated calendar management" },
        { icon: Briefcase, title: "Application Pipeline", desc: "End-to-end workflow tracking" },
        { icon: Users, title: "Talent Pool CRM", desc: "Reusable candidate database" },
        { icon: Target, title: "Weighted Scoring", desc: "Customizable role priorities" },
        { icon: BarChart3, title: "Hiring Analytics", desc: "Pipeline metrics and insights" }
      ]
    },
    {
      title: "Platform Security",
      icon: ShieldCheck,
      count: 4,
      features: [
        { icon: ShieldCheck, title: "Enterprise Security", desc: "Role-based access control" },
        { icon: Mail, title: "In-Platform Communication", desc: "Centralized candidate contact" },
        { icon: Building, title: "Branded Listings", desc: "Company profile integration" },
        { icon: Search, title: "Deep Profiling", desc: "Comprehensive AI analysis" }
      ]
    }
  ]

  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })

  return (
    <section ref={ref} className="relative py-24 lg:py-32 bg-gradient-to-b from-transparent via-primary/5 to-transparent">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2 variants={fadeInUp} className="text-4xl lg:text-5xl font-bold mb-4">
            Feature Categories
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Organized by role and functionality for seamless navigation
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="grid md:grid-cols-2 gap-8"
        >
          {categories.map((category, index) => (
            <CategoryCard key={index} category={category} isRTL={isRTL} />
          ))}
        </motion.div>
      </div>
    </section>
  )
}

// Category Card Component
function CategoryCard({ category, isRTL }: { category: any; isRTL: boolean }) {
  return (
    <motion.div
      variants={fadeInUp}
      className="group relative"
    >
      <Card className="relative overflow-hidden backdrop-blur-xl bg-white/5 border-white/10 hover:bg-gradient-to-br hover:from-primary/10 hover:to-transparent transition-all duration-500 hover:shadow-2xl hover:shadow-primary/20 h-full">
        <div className="p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5">
                <category.icon className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="text-2xl font-bold">{category.title}</h3>
                <p className="text-sm text-muted-foreground">{category.count} features</p>
              </div>
            </div>
            <Badge variant="outline" className="border-primary/20 text-primary">
              {category.count}
            </Badge>
          </div>

          {/* Features Grid */}
          <div className="grid gap-4">
            {category.features.map((feature: any, idx: number) => (
              <div
                key={idx}
                className="flex items-start gap-3 p-3 rounded-xl hover:bg-primary/5 transition-colors duration-200"
              >
                <div className="p-2 rounded-lg bg-primary/10 flex-shrink-0">
                  <feature.icon className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <h4 className="font-semibold text-sm mb-1">{feature.title}</h4>
                  <p className="text-xs text-muted-foreground">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Hover Gradient Effect */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/0 via-primary/0 to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
      </Card>
    </motion.div>
  )
}

// AI Capabilities Spotlight Component
function AiCapabilitiesSpotlight({ isRTL }: { isRTL: boolean }) {
  const criteria = [
    { name: "Job Fit", score: 95, icon: Target },
    { name: "Experience", score: 88, icon: Briefcase },
    { name: "Education", score: 92, icon: GraduationCap },
    { name: "Skills Match", score: 90, icon: Award },
    { name: "Technical Proficiency", score: 87, icon: Brain },
    { name: "Soft Skills", score: 85, icon: Users },
    { name: "Career Growth", score: 89, icon: TrendingUp },
    { name: "Potential", score: 91, icon: Building },
    { name: "Overall Score", score: 93, icon: Sparkles }
  ]

  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })

  return (
    <section ref={ref} className="relative py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2 variants={fadeInUp} className="text-4xl lg:text-5xl font-bold mb-4">
            Powered by Advanced AI
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg text-muted-foreground max-w-2xl mx-auto">
            9 comprehensive scoring criteria for objective candidate evaluation
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {criteria.map((criterion, index) => (
            <AiCriterionCard key={index} criterion={criterion} index={index} isInView={isInView} />
          ))}
        </motion.div>
      </div>
    </section>
  )
}

// AI Criterion Card Component
function AiCriterionCard({ criterion, index, isInView }: { criterion: any; index: number; isInView: boolean }) {
  const [score, setScore] = useState(0)

  useEffect(() => {
    if (isInView) {
      const timer = setTimeout(() => {
        setScore(criterion.score)
      }, index * 100)
      return () => clearTimeout(timer)
    }
  }, [isInView, criterion.score, index])

  return (
    <motion.div variants={fadeInUp}>
      <Card className="relative overflow-hidden backdrop-blur-xl bg-white/5 border-white/10 hover:bg-gradient-to-br hover:from-primary/10 hover:to-transparent transition-all duration-500 hover:shadow-xl hover:shadow-primary/20 p-6 group">
        <div className="flex items-center justify-between mb-4">
          <div className="p-2 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors duration-300">
            <criterion.icon className="w-5 h-5 text-primary" />
          </div>
          <Badge variant="outline" className="border-primary/20 text-primary font-bold">
            {score}%
          </Badge>
        </div>

        <h3 className="font-semibold mb-3">{criterion.name}</h3>

        {/* Progress Bar */}
        <div className="relative h-2 bg-muted/20 rounded-full overflow-hidden">
          <motion.div
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-primary to-primary/60 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: isInView ? `${score}%` : 0 }}
            transition={{ duration: 1, delay: index * 0.1 }}
          />
        </div>
      </Card>
    </motion.div>
  )
}

// CTA Section Component
function CtaSection({ isRTL }: { isRTL: boolean }) {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })

  return (
    <section ref={ref} className="relative py-24 lg:py-32 overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-primary/10 to-transparent" />
      <motion.div
        animate={floatingAnimation}
        className="absolute top-10 right-10 w-96 h-96 bg-primary/20 rounded-full blur-3xl"
      />
      <motion.div
        animate={{ ...floatingAnimation, transition: { ...floatingAnimation.transition, delay: 1.5 } }}
        className="absolute bottom-10 left-10 w-96 h-96 bg-primary/10 rounded-full blur-3xl"
      />

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp}>
            <Badge className="mb-6 px-4 py-2 text-sm font-medium bg-primary/10 hover:bg-primary/20 border-primary/20">
              <Sparkles className="w-4 h-4 mr-2 inline-block" />
              Get Started Today
            </Badge>
          </motion.div>

          <motion.h2 variants={fadeInUp} className="text-4xl lg:text-5xl font-bold mb-6">
            Ready to Transform Your Hiring?
          </motion.h2>

          <motion.p variants={fadeInUp} className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Join hundreds of companies already using Skill Fits to make smarter hiring decisions
          </motion.p>

          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="group">
              <Link href="/employer/request-free-credit">
                View Plans
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link href="/request-demo">
                Request Demo
              </Link>
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}

// Main Features Page Component
export default function FeaturesPage() {
  const { i18n } = useTranslation()
  const isRTL = i18n.language === 'ar'

  return (
    <div className="relative overflow-hidden">
      <HeroSection isRTL={isRTL} />

      {/* Wave Divider */}
      <div className="relative h-24">
        <svg
          className="absolute bottom-0 w-full h-24"
          viewBox="0 0 1440 120"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0,64L48,69.3C96,75,192,85,288,80C384,75,480,53,576,48C672,43,768,53,864,58.7C960,64,1056,64,1152,58.7C1248,53,1344,43,1392,37.3L1440,32L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"
            fill="currentColor"
            className="text-primary/5"
          />
        </svg>
      </div>

      <InteractiveShowcase isRTL={isRTL} />

      <FeatureCategories isRTL={isRTL} />

      <AiCapabilitiesSpotlight isRTL={isRTL} />

      <CtaSection isRTL={isRTL} />
    </div>
  )
}
